import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import HowToSteps, { HowToStep } from '@comp/learn/how-to-steps'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

interface PageContent {
  metaTitle: string
  metaDescription: string
  heroEyebrow: string
  heroTitle: [string, string]
  heroBody: string
  badges: string[]
  primaryCta: string
  secondaryCta: string
  stepsEyebrow: string
  stepsTitle: string
  stepsBody: string
  docLabel: string
  steps: HowToStep[]
  relatedEyebrow: string
  relatedTitle: string
  related: RelatedItem[]
}

const content: Record<'en' | 'tr', PageContent> = {
  en: {
    metaTitle: 'Keyless Google Cloud access with Workload Identity Federation',
    metaDescription:
      'Let on-prem services, CI/CD agents, and apps in other clouds call GCP APIs with a Monosign-issued token instead of a service account key. Pool, provider, and impersonation setup, step by step.',
    heroEyebrow: 'How-to · Keyless GCP workloads',
    heroTitle: ['GCP access for workloads.', 'No service account keys.'],
    heroBody:
      'Workload Identity Federation lets anything running outside GCP (an on-prem service, a CI/CD agent, an app in another cloud) reach Google APIs with a short-lived token issued by Monosign. The downloadable service account key, the one that leaks into repos and backups, disappears from the architecture.',
    badges: ['OIDC · client_credentials', 'Closed-network option (inline JWKS)', 'Verified end to end'],
    primaryCta: 'Start free trial →',
    secondaryCta: 'Open the docs',
    stepsEyebrow: 'Walkthrough',
    stepsTitle: 'From client credentials to GCP API call.',
    stepsBody: 'Five steps. The token chain does the rest at runtime.',
    docLabel: 'Continue in the full docs',
    steps: [
      {
        title: 'Prepare Monosign: certificate, issuer, API application',
        body: 'Configure an organization signing certificate and pin the global JWT issuer: Google validates signatures against your JWKS and rejects tokens whose iss varies by hostname. Then create an API-type application with an OIDC key; its Client Id and Secret are the workload’s credentials.',
        hint: 'Without a pinned certificate, Monosign generates an ephemeral signing key on every restart and the federation silently breaks.',
      },
      {
        title: 'Set the audience on the OIDC key',
        body: 'Enter the full resource name of the GCP provider (https://iam.googleapis.com/projects/PROJECT_NUMBER/…/providers/PROVIDER_ID) in the key’s Audience field. Google requires the token’s aud claim to match the provider exactly, and client_credentials tokens carry no aud until this field is set.',
        hint: 'You need the project number, not the project ID; gcloud projects describe shows it.',
      },
      {
        title: 'Create the workload identity pool and provider',
        body: 'On the GCP project, create a workload identity pool and an OIDC provider for it. Two models work: a public issuer that Google discovers over the internet, or, unique to this scenario, a closed-network setup where you upload Monosign’s JWKS inline and the issuer never needs to be reachable from outside.',
        hint: 'The inline-JWKS model means an air-gapped or on-prem-only Monosign can still federate into GCP.',
      },
      {
        title: 'Bind a service account for impersonation',
        body: 'Create a service account with the roles the workload actually needs, and grant the pool identity workloadIdentityUser on it. The workload’s federated token then exchanges for short-lived service account credentials: permissions stay on the service account, identity stays in Monosign.',
      },
      {
        title: 'Wire the token chain into the workload',
        body: 'At runtime the workload requests a Monosign token via client_credentials, exchanges it at Google STS for a federated token, and calls generateAccessToken for the service account. Google’s client libraries drive the whole chain from one credential-configuration JSON: no code changes beyond pointing at the file.',
        hint: 'Keep token lifetimes short; Google rejects federated tokens that live longer than 24 hours, and the 5-minute Monosign default is fine.',
      },
    ],
    relatedEyebrow: 'Keep exploring',
    relatedTitle: 'Related Monofor capabilities.',
    related: [
      { label: 'Google Cloud console SSO', description: 'The companion guide: Workforce Identity Federation for humans.', href: '/learn/gcp-workforce-identity-federation' },
      { label: 'Machine identity', description: 'Why non-human credentials need lifecycle too.', href: '/glossary/machine-identity' },
      { label: 'REST API getting started', description: 'Authenticate and automate against Monosign.', href: '/learn/rest-api-getting-started' },
    ],
  },
  tr: {
    metaTitle: 'Workload Identity Federation ile anahtarsız Google Cloud erişimi',
    metaDescription:
      "Şirket içi servisler, CI/CD ajanları ve diğer buluttaki uygulamalar GCP API'lerini servis hesabı anahtarı yerine Monosign'ın verdiği token ile çağırsın. Pool, provider ve impersonation kurulumu adım adım.",
    heroEyebrow: 'Rehber · Anahtarsız GCP',
    heroTitle: ["Workload'lar için GCP erişimi.", 'Servis hesabı anahtarı olmadan.'],
    heroBody:
      "Workload Identity Federation, GCP dışında çalışan her şeyin (şirket içi bir servis, bir CI/CD ajanı, başka buluttaki bir uygulama) Google API'lerine Monosign'ın verdiği kısa ömürlü token ile ulaşmasını sağlar. Repolara ve yedeklere sızan indirilebilir servis hesabı anahtarı mimariden tamamen çıkar.",
    badges: ['OIDC · client_credentials', 'Kapalı ağ seçeneği (inline JWKS)', 'Uçtan uca doğrulandı'],
    primaryCta: 'Ücretsiz deneme başlat →',
    secondaryCta: 'Dokümantasyonu açın',
    stepsEyebrow: 'Adım adım',
    stepsTitle: "Client credentials'tan GCP API çağrısına.",
    stepsBody: 'Beş adım. Çalışma anında gerisini token zinciri halleder.',
    docLabel: 'Rehberin tamamına gidin',
    steps: [
      {
        title: "Monosign'ı hazırlayın: sertifika, issuer, API uygulaması",
        body: "Organizasyon imza sertifikası tanımlayın ve global JWT issuer değerini sabitleyin: Google imzayı JWKS'inizle doğrular ve iss değeri hostname'e göre değişen token'ları reddeder. Ardından API tipinde bir uygulama ve üzerinde bir OIDC anahtarı oluşturun; Client Id ve Secret, workload'un kimlik bilgisidir.",
        hint: 'Sertifika sabitlenmezse Monosign her yeniden başlatmada geçici imza anahtarı üretir ve federasyon sessizce kırılır.',
      },
      {
        title: "OIDC anahtarında audience'ı ayarlayın",
        body: "GCP provider'ının tam kaynak adını (https://iam.googleapis.com/projects/PROJE_NUMARASI/…/providers/PROVIDER_ID) anahtarın Audience alanına girin. Google, token'daki aud claim'inin provider ile birebir eşleşmesini şart koşar; client_credentials token'ları bu alan doldurulana kadar aud taşımaz.",
        hint: 'Proje ID’si değil proje numarası gerekir; gcloud projects describe gösterir.',
      },
      {
        title: "Workload identity pool ve provider'ı oluşturun",
        body: "GCP projesinde bir workload identity pool ve ona bağlı bir OIDC provider oluşturun. İki model çalışır: Google'ın internetten keşfettiği açık issuer, ya da (bu senaryoya özgü) Monosign'ın JWKS'ini inline yüklediğiniz ve issuer'ın dışarıdan hiç erişilebilir olmadığı kapalı ağ kurulumu.",
        hint: "Inline JWKS modeli, yalnızca şirket içinde yaşayan bir Monosign'ın bile GCP'ye federasyon kurabilmesi demektir.",
      },
      {
        title: 'Impersonation için servis hesabı bağlayın',
        body: "Workload'un gerçekten ihtiyaç duyduğu rollere sahip bir servis hesabı oluşturun ve havuz kimliğine bu hesap üzerinde workloadIdentityUser verin. Workload'un federated token'ı, kısa ömürlü servis hesabı kimlik bilgileriyle değiştirilir: yetkiler servis hesabında, kimlik Monosign'da kalır.",
      },
      {
        title: "Token zincirini workload'a bağlayın",
        body: "Çalışma anında workload, client_credentials ile Monosign token'ı alır, Google STS'te federated token ile değiştirir ve servis hesabı için generateAccessToken çağırır. Google istemci kütüphaneleri bütün zinciri tek bir credential-configuration JSON dosyasından yürütür; dosyayı göstermek dışında kod değişikliği yoktur.",
        hint: "Token ömürlerini kısa tutun; Google 24 saatten uzun yaşayan federated token'ları reddeder, Monosign'ın 5 dakikalık varsayılanı yeterlidir.",
      },
    ],
    relatedEyebrow: 'Keşfetmeye devam edin',
    relatedTitle: 'İlgili Monofor yetenekleri.',
    related: [
      { label: 'Google Cloud konsoluna SSO', description: 'Eş rehber: insanlar için Workforce Identity Federation.', href: '/learn/gcp-workforce-identity-federation' },
      { label: 'Makine kimliği', description: 'İnsan dışı kimlik bilgilerinin de yaşam döngüsü gerekir.', href: '/glossary/machine-identity' },
      { label: "REST API'ye başlangıç", description: "Monosign'a karşı kimlik doğrulayın ve otomatikleştirin.", href: '/learn/rest-api-getting-started' },
    ],
  },
}

type Props = { locale: string }

export default function GcpWorkloadFederationPage({ locale }: Props) {
  const c = locale === 'tr' ? content.tr : content.en
  return (
    <Layout>
      <SolutionMeta
        title={c.metaTitle}
        description={c.metaDescription}
        path="/learn/gcp-workload-identity-federation"
        locale={locale}
        bilingual
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow={c.heroEyebrow}
          title={
            <>
              {c.heroTitle[0]}
              <br />
              {c.heroTitle[1]}
            </>
          }
          body={c.heroBody}
          badges={c.badges}
          primaryCta={{ label: c.primaryCta, href: '/try/now' }}
          secondaryCta={{ label: c.secondaryCta, href: 'https://help.monofor.com/monosign' }}
        />
        <HowToSteps
          eyebrow={c.stepsEyebrow}
          title={<>{c.stepsTitle}</>}
          body={c.stepsBody}
          steps={c.steps}
          schemaName={c.metaTitle}
          docHref="https://help.monofor.com/monosign"
          docLabel={c.docLabel}
        />
        <SolutionRelated
          eyebrow={c.relatedEyebrow}
          title={c.relatedTitle}
          items={c.related}
        />
        <FinalCta />
      </main>
    </Layout>
  )
}

export async function getStaticProps({ locale }: { locale: string }) {
  return { props: { locale: locale ?? 'en' } }
}
